import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { FiCheck, FiX, FiSave, FiRefreshCw, FiSlash, FiMail, FiTrash2, FiEyeOff } from 'react-icons/fi'
import axios from '../config/axios'

const ADMIN_PINS = ['1977', '1966']

function AdminPage() {
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()
  const [users, setUsers] = useState([])
  const [manufacturers, setManufacturers] = useState([])
  const [saving, setSaving] = useState({})
  const [saved, setSaved] = useState({})
  const [loading, setLoading] = useState(true)
  const [localAllowed, setLocalAllowed] = useState({})
  const [unlocked, setUnlocked] = useState(false)
  const [pinInput, setPinInput] = useState('')
  const [pinError, setPinError] = useState(false)
  const [blockedUsers, setBlockedUsers] = useState(new Set())
  const [approvalLoading, setApprovalLoading] = useState({})
  const [userStatuses, setUserStatuses] = useState({}) // uid -> { approved, declined }
  const [reportState, setReportState] = useState('idle') // idle | sending | sent | error
  const [deleteModal, setDeleteModal] = useState(null) // { uid, name, mode: 'purge'|'delete' }
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleSendReport = async () => {
    setReportState('sending')
    try {
      await fetch('https://case-sizing-engine-api.vercel.app/api/auth/weekly-report?secret=23590fdba3bbf46033a6d0fec43776c1b8bfe1944e67d9b09c6fe3888598a484&days=7')
      setReportState('sent')
      setTimeout(() => setReportState('idle'), 3000)
    } catch {
      setReportState('error')
      setTimeout(() => setReportState('idle'), 3000)
    }
  }

  const handlePinSubmit = (e) => {
    e.preventDefault()
    if (ADMIN_PINS.includes(pinInput)) {
      setUnlocked(true)
      setPinError(false)
    } else {
      setPinError(true)
      setPinInput('')
    }
  }

  const focusUid = searchParams.get('uid')

  useEffect(() => {
    Promise.all([
      axios.get('/api/admin/users'),
      axios.get('/api/search/series')
    ]).then(([usersRes, seriesRes]) => {
      const fetchedUsers = [...usersRes.data.users].sort((a, b) =>
        (a.role === 'admin' ? 0 : 1) - (b.role === 'admin' ? 0 : 1)
      )
      setUsers(fetchedUsers)
      // Load approval statuses
      const statuses = {}
      fetchedUsers.forEach(u => { statuses[u.uid] = { approved: u.approved, declined: u.declined } })
      setUserStatuses(statuses)
      // Load blocked status
      const blocked = new Set()
      fetchedUsers.forEach(u => {
        if (u.blocked) blocked.add(u.uid)
      })
      setBlockedUsers(blocked)
      // Extract manufacturers from processes and flatten into single array
      const allManufacturers = []
      const seenMfrs = new Set()
      const processes = seriesRes.data.processes || []
      processes.forEach(proc => {
        proc.manufacturers.forEach(mfr => {
          if (!seenMfrs.has(mfr.name)) {
            allManufacturers.push(mfr)
            seenMfrs.add(mfr.name)
          }
        })
      })
      setManufacturers(allManufacturers)
      const allowed = {}
      fetchedUsers.forEach(u => { allowed[u.uid] = [...(u.allowedManufacturers || [])] })
      setLocalAllowed(allowed)
    }).catch(console.error).finally(() => setLoading(false))
  }, [user.uid])

  useEffect(() => {
    if (!focusUid) return
    const id = setTimeout(() => {
      document.getElementById(`user-${focusUid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 500)
    return () => clearTimeout(id)
  }, [focusUid, users])

  const toggleManufacturer = (uid, name) => {
    setLocalAllowed(prev => {
      const current = prev[uid] || []
      return {
        ...prev,
        [uid]: current.includes(name) ? current.filter(m => m !== name) : [...current, name]
      }
    })
  }

  const saveUser = async (uid) => {
    setSaving(prev => ({ ...prev, [uid]: true }))
    try {
      await axios.post(`/api/admin/users/${uid}/manufacturers`, {
        allowedManufacturers: localAllowed[uid] || []
      })
      setSaved(prev => ({ ...prev, [uid]: true }))
      setTimeout(() => setSaved(prev => ({ ...prev, [uid]: false })), 2000)
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(prev => ({ ...prev, [uid]: false }))
    }
  }

  const toggleBlock = async (uid) => {
    setSaving(prev => ({ ...prev, [uid]: true }))
    try {
      const isBlocked = blockedUsers.has(uid)
      await axios.post(`/api/admin/users/${uid}/block`, {
        blocked: !isBlocked
      })
      setBlockedUsers(prev => {
        const newSet = new Set(prev)
        if (isBlocked) {
          newSet.delete(uid)
        } else {
          newSet.add(uid)
        }
        return newSet
      })
      setSaved(prev => ({ ...prev, [uid]: true }))
      setTimeout(() => setSaved(prev => ({ ...prev, [uid]: false })), 2000)
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(prev => ({ ...prev, [uid]: false }))
    }
  }

  const handleApproval = async (uid, approve) => {
    setApprovalLoading(prev => ({ ...prev, [uid]: true }))
    try {
      await axios.post(`/api/admin/users/${uid}/approval`, {
        approved: approve
      })
      setUserStatuses(prev => ({
        ...prev,
        [uid]: { approved: approve, declined: !approve }
      }))
    } catch (e) {
      console.error(e)
    } finally {
      setApprovalLoading(prev => ({ ...prev, [uid]: false }))
    }
  }

  const openDeleteModal = (uid, name, mode) => {
    setDeleteModal({ uid, name, mode })
    setDeleteConfirm('')
  }

  const closeDeleteModal = () => {
    setDeleteModal(null)
    setDeleteConfirm('')
  }

  const handlePurge = async () => {
    setDeleteLoading(true)
    try {
      await axios.post(`/api/admin/users/${deleteModal.uid}/purge`, {})
      setUsers(prev => prev.filter(u => u.uid !== deleteModal.uid))
      closeDeleteModal()
    } catch (e) {
      console.error(e)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleDelete = async () => {
    if (deleteConfirm !== 'yes') return
    setDeleteLoading(true)
    try {
      await axios.delete(`/api/admin/users/${deleteModal.uid}`, {
        data: { confirm: 'yes' }
      })
      setUsers(prev => prev.filter(u => u.uid !== deleteModal.uid))
      closeDeleteModal()
    } catch (e) {
      console.error(e)
    } finally {
      setDeleteLoading(false)
    }
  }

  if (!unlocked) {
    return (
      <main className="min-h-screen bg-bg-dark flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="card">
            <h2 className="text-xl font-bold text-accent-yellow mb-2">Admin Access</h2>
            <p className="text-gray-400 text-sm mb-6">Enter your admin Password to continue.</p>
            {pinError && (
              <div className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded text-red-200 text-sm">
                Incorrect PIN. Try again.
              </div>
            )}
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pinInput}
                onChange={e => { setPinInput(e.target.value); setPinError(false) }}
                placeholder="Enter PIN"
                className="input-field text-center text-xl tracking-widest"
                autoFocus
              />
              <button type="submit" className="w-full btn-primary">
                Unlock
              </button>
            </form>
          </div>
        </div>
      </main>
    )
  }

  if (loading) return (
    <div className="min-h-screen bg-bg-dark flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-yellow"></div>
    </div>
  )

  return (
    <main className="max-w-6xl mx-auto px-4 py-4">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-lg font-bold text-accent-yellow">Admin — User Access Management (v3)</h1>
        <button
          onClick={handleSendReport}
          disabled={reportState !== 'idle'}
          className={`flex items-center gap-2 px-3 py-2 md:py-1.5 rounded text-sm font-medium transition-colors flex-shrink-0 ${
            reportState === 'sent'  ? 'bg-green-700 text-white cursor-default' :
            reportState === 'error' ? 'bg-red-700 text-white cursor-default' :
            reportState === 'sending' ? 'bg-gray-600 text-gray-300 cursor-wait' :
            'bg-[#e07b02] hover:bg-[#c96f02] active:bg-[#c96f02] text-white'
          }`}
        >
          {reportState === 'sending' ? <FiRefreshCw size={16} className="animate-spin" /> :
           reportState === 'sent'    ? <FiCheck size={16} /> :
           reportState === 'error'   ? <FiX size={16} /> :
           <FiMail size={16} />}
          <span className="hidden md:inline">
            {reportState === 'sending' ? 'Sending…' :
             reportState === 'sent'    ? 'Report Sent!' :
             reportState === 'error'   ? 'Failed' :
             'Weekly Report'}
          </span>
        </button>
      </div>

      <div className="border border-border-dark rounded overflow-hidden">
        {/* Table header - hidden on mobile */}
        <div className="hidden md:grid grid-cols-[2fr_2fr_1fr_auto_auto] gap-0 bg-bg-card border-b border-border-dark px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
          <span>User</span>
          <span>Manufacturer Access</span>
          <span>Status</span>
          <span></span>
          <span></span>
        </div>


        {users.map((u, i) => {
          const isTarget = u.uid === focusUid
          const allowed = localAllowed[u.uid] || []
          const status = userStatuses[u.uid] || { approved: u.approved, declined: u.declined }
          const isPending = !status.approved && !status.declined && u.role !== 'admin'
          const isDeclined = status.declined && u.role !== 'admin'
          return (
            <div
              key={u.uid}
              id={`user-${u.uid}`}
              className={`flex flex-col md:grid md:grid-cols-[2fr_2fr_1fr_auto_auto] gap-3 md:gap-0 md:items-center px-4 py-3 md:px-3 md:py-2 border-b border-border-dark last:border-b-0 transition-all ${isTarget ? 'ring-1 ring-inset ring-accent-teal' : ''} ${blockedUsers.has(u.uid) ? 'bg-red-950/60 border-l-4 border-l-red-500' : i % 2 === 0 ? 'bg-bg-dark' : 'bg-bg-card'}`}
            >
              {/* User info */}
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`font-semibold text-sm break-words ${blockedUsers.has(u.uid) ? 'text-red-400' : 'text-white'}`}>{u.fullName || '—'}</p>
                  {blockedUsers.has(u.uid) && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-700 text-white text-xs font-bold rounded">
                      <FiSlash size={10} /> BLOCKED
                    </span>
                  )}
                </div>
                <p className={`text-xs break-words ${blockedUsers.has(u.uid) ? 'text-red-300' : 'text-accent-teal'}`}>{u.companyName || '—'}{u.cellNumber ? ` • ${u.cellNumber}` : ''}</p>
                <p className={`text-xs break-words ${blockedUsers.has(u.uid) ? 'text-red-400' : 'text-gray-500'}`}>{u.email}</p>
                {u.lastLogin && (
                  <p className="text-xs text-gray-600 mt-0.5">
                    Last login: {new Date(u.lastLogin).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                )}
              </div>

              {/* Manufacturer checkboxes */}
              <div className="md:pr-3">
                {u.role === 'admin' ? (
                  <span className="text-xs text-gray-500 italic">Full access</span>
                ) : (
                  <div className="flex flex-wrap gap-x-3 gap-y-2">
                    {manufacturers.map(mfr => (
                      <label key={mfr.name} className="flex items-center gap-1.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={allowed.includes(mfr.name)}
                          onChange={() => toggleManufacturer(u.uid, mfr.name)}
                          className="w-3.5 h-3.5 accent-accent-teal flex-shrink-0"
                        />
                        <span className="text-xs text-gray-300 group-hover:text-white transition break-words">{mfr.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Status badge + approve/decline/re-approve buttons */}
              <div className="flex flex-col gap-1.5">
                <span className={`text-xs px-2 py-0.5 rounded font-semibold self-start ${
                  u.role === 'admin' ? 'bg-purple-900/40 text-purple-300 border border-purple-700' :
                  status.approved ? 'bg-teal-900/40 text-teal-300 border border-teal-700' :
                  status.declined ? 'bg-red-900/40 text-red-300 border border-red-700' :
                  'bg-yellow-900/40 text-yellow-300 border border-yellow-700'
                }`}>
                  {u.role === 'admin' ? 'Admin' : status.approved ? 'Approved' : status.declined ? 'Declined' : 'Pending'}
                </span>
                {isPending && (
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => handleApproval(u.uid, true)}
                      disabled={approvalLoading[u.uid]}
                      className="flex items-center gap-1 px-2 py-0.5 rounded bg-teal-700 hover:bg-teal-600 text-white text-xs font-semibold transition disabled:opacity-50"
                    >
                      {approvalLoading[u.uid] ? <FiRefreshCw size={10} className="animate-spin" /> : <FiCheck size={10} />}
                      Approve
                    </button>
                    <button
                      onClick={() => handleApproval(u.uid, false)}
                      disabled={approvalLoading[u.uid]}
                      className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-700 hover:bg-red-600 text-white text-xs font-semibold transition disabled:opacity-50"
                    >
                      {approvalLoading[u.uid] ? <FiRefreshCw size={10} className="animate-spin" /> : <FiX size={10} />}
                      Decline
                    </button>
                  </div>
                )}
                {isDeclined && (
                  <button
                    onClick={() => handleApproval(u.uid, true)}
                    disabled={approvalLoading[u.uid]}
                    className="flex items-center gap-1 px-2 py-0.5 rounded bg-teal-700 hover:bg-teal-600 text-white text-xs font-semibold transition disabled:opacity-50 self-start"
                  >
                    {approvalLoading[u.uid] ? <FiRefreshCw size={10} className="animate-spin" /> : <FiCheck size={10} />}
                    Re-approve
                  </button>
                )}
              </div>

              {/* Save button */}
              <div className="md:pl-3">
                {u.role !== 'admin' && (
                  <button
                    onClick={() => saveUser(u.uid)}
                    disabled={saving[u.uid]}
                    className="w-full md:w-auto flex items-center justify-center md:justify-start gap-1 px-3 py-2 md:py-1 rounded bg-accent-teal text-white text-xs font-semibold hover:bg-teal-600 transition disabled:opacity-50"
                  >
                    {saving[u.uid] ? <FiRefreshCw size={11} className="animate-spin" /> : saved[u.uid] ? <FiCheck size={11} /> : <FiSave size={11} />}
                    {saving[u.uid] ? 'Saving…' : saved[u.uid] ? 'Saved!' : 'Save'}
                  </button>
                )}
              </div>

              {/* Block / Purge / Delete buttons */}
              <div className="md:pl-2 flex flex-col md:flex-row gap-1.5">
                {u.role !== 'admin' && (
                  <>
                    <button
                      onClick={() => toggleBlock(u.uid)}
                      disabled={saving[u.uid]}
                      className={`w-full md:w-auto flex items-center justify-center gap-1 px-3 py-2 md:py-1 rounded text-white text-xs font-semibold transition disabled:opacity-50 ${
                        blockedUsers.has(u.uid) ? 'bg-red-700 hover:bg-red-800' : 'bg-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      <FiSlash size={11} />
                      {blockedUsers.has(u.uid) ? 'Unblock' : 'Block'}
                    </button>
                    <button
                      onClick={() => openDeleteModal(u.uid, u.fullName || u.email, 'purge')}
                      className="w-full md:w-auto flex items-center justify-center gap-1 px-3 py-2 md:py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold transition"
                      title="Hide from admin view (keeps Firebase account)"
                    >
                      <FiEyeOff size={11} />
                      Purge
                    </button>
                    <button
                      onClick={() => openDeleteModal(u.uid, u.fullName || u.email, 'delete')}
                      className="w-full md:w-auto flex items-center justify-center gap-1 px-3 py-2 md:py-1 rounded bg-red-900 hover:bg-red-800 text-red-300 text-xs font-semibold transition"
                      title="Permanently delete from Firebase Auth + Firestore"
                    >
                      <FiTrash2 size={11} />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Purge / Delete confirmation modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-sm bg-bg-card border border-border-dark rounded-lg p-6 shadow-2xl">
            {deleteModal.mode === 'purge' ? (
              <>
                <h3 className="text-base font-bold text-accent-yellow mb-2">Purge Account</h3>
                <p className="text-sm text-gray-300 mb-1">
                  <span className="text-white font-semibold">{deleteModal.name}</span> will be hidden from the admin view.
                </p>
                <p className="text-xs text-gray-500 mb-5">Their Firebase account is kept intact. This cannot be undone from the UI.</p>
                <div className="flex gap-3">
                  <button
                    onClick={closeDeleteModal}
                    className="flex-1 px-3 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePurge}
                    disabled={deleteLoading}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded bg-gray-600 hover:bg-gray-500 text-gray-200 text-sm font-semibold transition disabled:opacity-50"
                  >
                    {deleteLoading ? <FiRefreshCw size={13} className="animate-spin" /> : <FiEyeOff size={13} />}
                    Purge
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-base font-bold text-red-400 mb-2">Permanently Delete Account</h3>
                <p className="text-sm text-gray-300 mb-1">
                  <span className="text-white font-semibold">{deleteModal.name}</span> will be deleted from Firestore and Firebase Auth.
                </p>
                <p className="text-xs text-red-400 mb-4">This is irreversible. Type <span className="font-bold text-white">yes</span> to confirm.</p>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder="Type yes to confirm"
                  className="input-field mb-4 text-sm"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    onClick={closeDeleteModal}
                    className="flex-1 px-3 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleteConfirm !== 'yes' || deleteLoading}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded bg-red-700 hover:bg-red-800 text-white text-sm font-semibold transition disabled:opacity-50"
                  >
                    {deleteLoading ? <FiRefreshCw size={13} className="animate-spin" /> : <FiTrash2 size={13} />}
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </main>
  )
}

export default AdminPage
